import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Paper,
  Chip,
  Button,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TerminalIcon from '@mui/icons-material/Terminal';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ShareIcon from '@mui/icons-material/Share';
import { GameVideoSummary, GoalHighlight, GamePeriod, GoalEventData, TimelineAuditEntry, TimelineEventItem, TimelineNote, TimelineClip } from '../../../types';
import { isGoalByJets, isJetsHomeGame, getGoalTeamName, getTargetTeamName } from '../../../utils/gameScoreUtils';

export interface InspectorSidebarProps {
  game: GameVideoSummary;
  highlights: any[];
  sidebarWidth: number;
  isSidebarCollapsed: boolean;
  onToggleCollapse: () => void;
  canManageEvents: boolean;
  onJumpToGoal: (timestamp: number) => void;
  onJumpToPeriod: (period: number) => void;
  onPlayHighlightClip: (index: number) => void;
  onDownloadHighlightClip?: (hl: GoalHighlight) => void;
  onOpenAddEvent: (type: string) => void;
  onDeleteEvent: (eventId: string) => void;
  deletingEventId: string | null;
  onOpenLogsModal: () => void;
  onCopyShareLink: (url: string) => void;
  initialTab?: number;
  isJetsHome?: boolean;

  // Multi-Track Studio Inspector Features
  periods?: GamePeriod[];
  selectedTimelineItem?:
    | { type: 'period'; periodNumber: number }
    | { type: 'goal'; goalNumber: number; eventId?: string }
    | { type: 'note'; id: string }
    | { type: 'clip'; id: string }
    | null;
  unlockedTimelineItem?: { type: 'period' | 'goal' | 'clip' | string; id: number | string } | null;
  unlockedTimelineItems?: Array<{ type: 'period' | 'goal' | 'clip' | string; id: number | string }>;
  onSaveAll?: () => Promise<void> | void;
  onDiscardAll?: () => void;
  onUpdatePeriodDraft?: (periodNumber: number, start: number, end: number) => void;
  onUpdateGoalDraft?: (
    goalIdOrNumber: number | string,
    clipStart?: number,
    clipEnd?: number,
    timestamp?: number,
    metadataUpdates?: Partial<GoalEventData>
  ) => void;
  savingAll?: boolean;
  onUnlockItem?: (type: 'period' | 'goal' | 'clip' | string, id: number | string) => void;
  onLockItem?: (type: 'period' | 'goal' | 'clip' | string, id: number | string) => void;
  onDiscardChanges?: () => void;
  onDeselectItem?: () => void;
  onUpdatePeriod?: (periodNumber: number, newStart: number, newEnd: number) => Promise<void>;
  onDeletePeriod?: (periodNumber: number) => Promise<void> | void;
  onUpdateGoal?: (goalIdOrNumber: number | string, updates: Partial<GoalEventData>) => Promise<void>;
  currentTime?: number;
  editHistory?: TimelineAuditEntry[];

  // Timeline Events Support
  timelineEvents?: TimelineEventItem[];
  onUpdateNote?: (id: string, updates: Partial<TimelineNote>) => Promise<void>;
  onUpdateClip?: (id: string, updates: Partial<TimelineClip>) => Promise<void>;
  onDeleteTimelineEvent?: (id: string) => Promise<void>;
  onOpenShareModal?: (event: TimelineEventItem) => void;
  onDownloadClipPrompt?: (clip: TimelineClip) => void;
  onJumpToTime?: (time: number) => void;
  onSelectItem?: (
    item:
      | { type: 'period'; periodNumber: number }
      | { type: 'goal'; goalNumber: number; eventId?: string }
      | { type: 'note'; id: string }
      | { type: 'clip'; id: string }
  ) => void;
  onConvertEventType?: (
    sourceType: 'goal' | 'clip' | 'note',
    sourceId: string | number,
    targetType: 'goal' | 'clip' | 'note'
  ) => Promise<void> | void;
}

const EMPTY_TIMELINE_EVENTS: TimelineEventItem[] = [];

const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

const parseTimeInput = (val: string): number => {
  const clean = val.trim();
  if (clean.includes(':')) {
    const parts = clean.split(':');
    const m = parseFloat(parts[0]) || 0;
    const s = parseFloat(parts[1]) || 0;
    return m * 60 + s;
  }
  return parseFloat(clean) || 0;
};

export const InspectorSidebar: React.FC<InspectorSidebarProps> = ({
  game,
  highlights,
  sidebarWidth,
  isSidebarCollapsed,
  onToggleCollapse,
  canManageEvents,
  onJumpToGoal,
  onJumpToPeriod,
  onPlayHighlightClip,
  onDownloadHighlightClip,
  onOpenAddEvent,
  onDeleteEvent,
  deletingEventId,
  onOpenLogsModal,
  onCopyShareLink,
  initialTab,
  isJetsHome: isJetsHomeProp,
  periods = [],
  selectedTimelineItem = null,
  unlockedTimelineItem,
  unlockedTimelineItems = [],
  onSaveAll,
  onDiscardAll,
  onUpdatePeriodDraft,
  onUpdateGoalDraft,
  savingAll = false,
  onUnlockItem,
  onLockItem,
  onDiscardChanges,
  onDeselectItem,
  onUpdatePeriod,
  onDeletePeriod,
  onUpdateGoal,
  currentTime = 0,
  editHistory = [],
  timelineEvents = EMPTY_TIMELINE_EVENTS,
  onUpdateNote,
  onUpdateClip,
  onDeleteTimelineEvent,
  onOpenShareModal,
  onDownloadClipPrompt,
  onJumpToTime,
  onSelectItem,
  onConvertEventType,
}) => {
  const isJetsHome = isJetsHomeProp ?? isJetsHomeGame(game);
  const focalTeamShort = getTargetTeamName(game, true);
  const focalTeamFull = getTargetTeamName(game, false);
  const resolveInitialTab = (tab?: number, hasSelected?: boolean): { tabIndex: number; filter?: 'all' | 'goals' | 'clips' | 'notes' } => {
    if (tab === 1) {
      return { tabIndex: 0, filter: 'clips' };
    }
    if (tab === 2) {
      return { tabIndex: 1 };
    }
    if (tab === 3) {
      return { tabIndex: 2 };
    }
    if (tab === 0) {
      return { tabIndex: 0, filter: 'all' };
    }
    return { tabIndex: hasSelected ? 2 : 0 };
  };

  const initialConfig = resolveInitialTab(initialTab, Boolean(selectedTimelineItem));
  const [sidebarTab, setSidebarTab] = useState<number>(initialConfig.tabIndex);
  const [eventFilter, setEventFilter] = useState<'all' | 'goals' | 'clips' | 'notes'>(initialConfig.filter || 'all');
  const [goalFilter, setGoalFilter] = useState<'all' | 'jets' | 'opponent'>('all');

  useEffect(() => {
    if (initialTab !== undefined) {
      const cfg = resolveInitialTab(initialTab, Boolean(selectedTimelineItem));
      setSidebarTab(cfg.tabIndex);
      if (cfg.filter) {
        setEventFilter(cfg.filter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // Local fallback unlocked state if unlockedTimelineItem prop is not provided
  const [localUnlockedItem, setLocalUnlockedItem] = useState<{ type: string; id: any } | null>(null);

  const unlockedItemsList = unlockedTimelineItems.length > 0
    ? unlockedTimelineItems
    : (unlockedTimelineItem ? [unlockedTimelineItem] : (localUnlockedItem ? [localUnlockedItem] : []));

  const isItemUnlocked = (type: string, id: number | string): boolean => {
    return unlockedItemsList.some((item) => item.type === type && String(item.id) === String(id));
  };

  const getGoalTargetId = (g: GoalEventData): string | number => g.eventId || g.id || g.goalNumber;
  const getGoalDraftKey = (g: GoalEventData): string => String(getGoalTargetId(g));

  const findGoalBySelection = (
    goalsList: GoalEventData[],
    sel?: { goalNumber: number; eventId?: string } | null
  ): GoalEventData | undefined => {
    if (!sel) return undefined;
    if (sel.eventId) {
      const byId = goalsList.find((g) => (g.eventId && g.eventId === sel.eventId) || (g.id && g.id === sel.eventId));
      if (byId) return byId;
    }
    return goalsList.find((g) => g.goalNumber === sel.goalNumber);
  };

  const findGoalHighlight = (
    g: GoalEventData | undefined | null,
    highlightsList: GoalHighlight[]
  ): GoalHighlight | undefined => {
    if (!g) return undefined;
    const gId = g.eventId || g.id;
    if (gId) {
      const byId = highlightsList.find((h) => h.eventId === gId);
      if (byId) return byId;
    }
    return highlightsList.find(
      (h) => h.goalNumber === g.goalNumber && Math.abs((h.videoTimestamp || 0) - (g.videoTimestamp || 0)) <= 35
    );
  };

  const getGoalProvenanceInfo = (g: GoalEventData) => {
    const isAuto = g.source ? g.source === 'auto' : !g.isManual;
    const isModified = Boolean(g.isModified);
    if (isAuto && isModified) {
      return {
        label: 'Auto • Edited',
        tooltip: 'Originally auto-detected goal (updated manually)',
        isAuto: true,
        isModified: true,
      };
    }
    if (isAuto) {
      return {
        label: 'Auto',
        tooltip: 'Auto-detected goal — review and edit if needed',
        isAuto: true,
        isModified: false,
      };
    }
    return {
      label: 'Manual',
      tooltip: 'Manually added goal',
      isAuto: false,
      isModified,
    };
  };

  // Draft stores for multi-item editing across selection changes
  const [periodDrafts, setPeriodDrafts] = useState<Record<number, { start: string; end: string }>>({});
  const [clipDrafts, setClipDrafts] = useState<Record<string, { start: string; end: string; title: string; notes: string }>>({});
  const [goalDrafts, setGoalDrafts] = useState<Record<string, any>>({});

  // Snapshots for Discard functionality inside Inspector
  const [periodSnapshot, setPeriodSnapshot] = useState<{ start: string; end: string } | null>(null);
  const [goalSnapshot, setGoalSnapshot] = useState<{
    time: string;
    team: string;
    clipStart: string;
    clipEnd: string;
    period: number;
    score: string;
    clock: string;
    scorer: string;
    jersey: string;
    assists: string;
    desc: string;
  } | null>(null);

  // Period Form State
  const initialPeriod = selectedTimelineItem?.type === 'period'
    ? periods.find((item) => item.period === selectedTimelineItem.periodNumber)
    : undefined;
  const [periodStartInput, setPeriodStartInput] = useState<string>(() => initialPeriod ? formatSeconds(initialPeriod.startTime) : '');
  const [periodEndInput, setPeriodEndInput] = useState<string>(() => initialPeriod ? formatSeconds(initialPeriod.endTime) : '');
  const [savingPeriod, setSavingPeriod] = useState<boolean>(false);
  const isPeriodStartFocusedRef = useRef<boolean>(false);
  const isPeriodEndFocusedRef = useRef<boolean>(false);

  // Goal Form State
  const initialGoal = selectedTimelineItem?.type === 'goal'
    ? findGoalBySelection(game.goals || [], selectedTimelineItem)
    : undefined;
  const initialGoalHighlight = findGoalHighlight(initialGoal, game.highlights || []);
  const [goalTypeInput, setGoalTypeInput] = useState<'goal' | 'clip' | 'note'>('goal');
  const [goalTimeInput, setGoalTimeInput] = useState<string>(() => initialGoal ? formatSeconds(initialGoal.videoTimestamp) : '');
  const [goalTeam, setGoalTeam] = useState<string>(() => {
    if (!initialGoal) return focalTeamShort;
    return isGoalByJets(initialGoal, isJetsHome, game?.targetTeam) ? focalTeamShort : (game.opponent || 'Opponent');
  });
  const [goalClipStartInput, setGoalClipStartInput] = useState<string>(() => {
    if (initialGoal?.clipStartSeconds !== undefined) return formatSeconds(initialGoal.clipStartSeconds);
    if (initialGoalHighlight?.clipStartSeconds !== undefined) return formatSeconds(initialGoalHighlight.clipStartSeconds);
    return initialGoal ? formatSeconds(Math.max(0, initialGoal.videoTimestamp - 15)) : '';
  });
  const [goalClipEndInput, setGoalClipEndInput] = useState<string>(() => {
    if (initialGoal?.clipEndSeconds !== undefined) return formatSeconds(initialGoal.clipEndSeconds);
    if (initialGoalHighlight?.clipEndSeconds !== undefined) return formatSeconds(initialGoalHighlight.clipEndSeconds);
    return initialGoal ? formatSeconds(initialGoal.videoTimestamp + 5) : '';
  });
  const [goalPeriod, setGoalPeriod] = useState<number>(() => initialGoal?.period || 1);
  const [goalScore, setGoalScore] = useState<string>(() => initialGoal?.scoreAfter || '');
  const [goalClock, setGoalClock] = useState<string>(() => initialGoal?.gameClock || '');
  const [goalScorer, setGoalScorer] = useState<string>(() => initialGoal?.scorer || '');
  const [goalJersey, setGoalJersey] = useState<string>(() => initialGoal?.scorerJersey || '');
  const [goalAssists, setGoalAssists] = useState<string>(() => (initialGoal?.assists || []).join(', '));
  const [goalDesc, setGoalDesc] = useState<string>(() => initialGoal?.description || '');
  const [savingGoal, setSavingGoal] = useState<boolean>(false);
  const isGoalTimeFocusedRef = useRef<boolean>(false);
  const isGoalClipStartFocusedRef = useRef<boolean>(false);
  const isGoalClipEndFocusedRef = useRef<boolean>(false);

  // Note Form State
  const initialNote = selectedTimelineItem?.type === 'note'
    ? (timelineEvents.find((item) => item.id === selectedTimelineItem.id && item.eventType === 'note') as TimelineNote | undefined)
    : undefined;
  const [noteTypeInput, setNoteTypeInput] = useState<'note' | 'goal' | 'clip'>('note');
  const [noteTimeInput, setNoteTimeInput] = useState<string>(() => initialNote ? formatSeconds(initialNote.time) : '');
  const [noteTextInput, setNoteTextInput] = useState<string>(() => initialNote?.text || '');
  const [savingNote, setSavingNote] = useState<boolean>(false);
  const [noteSnapshot, setNoteSnapshot] = useState<{ time: string; text: string } | null>(null);
  const isNoteTimeFocusedRef = useRef<boolean>(false);

  // Clip Form State
  const initialClip = selectedTimelineItem?.type === 'clip'
    ? (timelineEvents.find((item) => item.id === selectedTimelineItem.id && item.eventType === 'clip') as TimelineClip | undefined)
    : undefined;
  const [clipTypeInput, setClipTypeInput] = useState<'clip' | 'goal' | 'note'>('clip');
  const [clipStartInput, setClipStartInput] = useState<string>(() => initialClip ? formatSeconds(initialClip.startTime) : '');
  const [clipEndInput, setClipEndInput] = useState<string>(() => initialClip ? formatSeconds(initialClip.endTime) : '');
  const [clipTitleInput, setClipTitleInput] = useState<string>(() => initialClip?.title || '');
  const [clipNotesInput, setClipNotesInput] = useState<string>(() => initialClip?.notes || '');
  const [savingClip, setSavingClip] = useState<boolean>(false);
  const [clipSnapshot, setClipSnapshot] = useState<{ start: string; end: string; title: string; notes: string } | null>(null);
  const isClipStartFocusedRef = useRef<boolean>(false);
  const isClipEndFocusedRef = useRef<boolean>(false);

  // Track selected key to only populate inputs once upon selection
  const selectedKey = selectedTimelineItem
    ? `${selectedTimelineItem.type}-${(selectedTimelineItem as any).periodNumber ?? (selectedTimelineItem as any).eventId ?? (selectedTimelineItem as any).goalNumber ?? (selectedTimelineItem as any).id}`
    : null;
  const prevSelectedKeyRef = useRef<string | null>(selectedKey);

  // Automatically switch to Inspector tab when an item is selected
  useEffect(() => {
    if (!selectedTimelineItem || !selectedKey) {
      prevSelectedKeyRef.current = null;
      return;
    }

    if (prevSelectedKeyRef.current === selectedKey) {
      return;
    }
    prevSelectedKeyRef.current = selectedKey;

    setSidebarTab(2);

    if (selectedTimelineItem.type === 'period') {
      const pNum = selectedTimelineItem.periodNumber;
      if (periodDrafts[pNum]) {
        setPeriodStartInput(periodDrafts[pNum].start);
        setPeriodEndInput(periodDrafts[pNum].end);
      } else {
        const p = periods.find((item) => item.period === pNum);
        if (p) {
          const s = formatSeconds(p.startTime);
          const e = formatSeconds(p.endTime);
          setPeriodStartInput(s);
          setPeriodEndInput(e);
        }
      }
    } else if (selectedTimelineItem.type === 'goal') {
      setGoalTypeInput('goal');
      const goalsList = game.goals || [];
      const g = findGoalBySelection(goalsList, selectedTimelineItem);
      const draftKey = g ? getGoalDraftKey(g) : String(selectedTimelineItem.eventId || selectedTimelineItem.goalNumber);
      const d = goalDrafts[draftKey] || goalDrafts[String(selectedTimelineItem.goalNumber)];
      if (d) {
        if (d.time !== undefined) setGoalTimeInput(d.time);
        if (d.team !== undefined) setGoalTeam(d.team);
        if (d.clipStart !== undefined) setGoalClipStartInput(d.clipStart || '');
        if (d.clipEnd !== undefined) setGoalClipEndInput(d.clipEnd || '');
        if (d.period !== undefined) setGoalPeriod(d.period);
        if (d.score !== undefined) setGoalScore(d.score);
        if (d.clock !== undefined) setGoalClock(d.clock);
        if (d.scorer !== undefined) setGoalScorer(d.scorer);
        if (d.jersey !== undefined) setGoalJersey(d.jersey);
        if (d.assists !== undefined) setGoalAssists(d.assists);
        if (d.desc !== undefined) setGoalDesc(d.desc);
      } else if (g) {
        const gh = findGoalHighlight(g, game.highlights || []);
        setGoalTimeInput(formatSeconds(g.videoTimestamp));
        setGoalTeam(isGoalByJets(g, isJetsHome, game?.targetTeam) ? focalTeamShort : (game.opponent || 'Opponent'));
        const startSec = g.clipStartSeconds !== undefined
          ? g.clipStartSeconds
          : (gh?.clipStartSeconds !== undefined ? gh.clipStartSeconds : Math.max(0, g.videoTimestamp - 15));
        const endSec = g.clipEndSeconds !== undefined
          ? g.clipEndSeconds
          : (gh?.clipEndSeconds !== undefined ? gh.clipEndSeconds : g.videoTimestamp + 5);
        setGoalClipStartInput(formatSeconds(startSec));
        setGoalClipEndInput(formatSeconds(endSec));
        setGoalPeriod(g.period || 1);
        setGoalScore(g.scoreAfter || '');
        setGoalClock(g.gameClock || '');
        setGoalScorer(g.scorer || '');
        setGoalJersey(g.scorerJersey || '');
        setGoalAssists((g.assists || []).join(', '));
        setGoalDesc(g.description || '');
      }
    } else if (selectedTimelineItem.type === 'note') {
      setNoteTypeInput('note');
      const n = timelineEvents.find((item) => item.id === selectedTimelineItem.id && item.eventType === 'note') as TimelineNote | undefined;
      if (n) {
        setNoteTimeInput(formatSeconds(n.time));
        setNoteTextInput(n.text || '');
      }
    } else if (selectedTimelineItem.type === 'clip') {
      setClipTypeInput('clip');
      const cId = selectedTimelineItem.id;
      if (clipDrafts[cId]) {
        const d = clipDrafts[cId];
        setClipStartInput(d.start);
        setClipEndInput(d.end);
        setClipTitleInput(d.title);
        setClipNotesInput(d.notes);
      } else {
        const c = timelineEvents.find((item) => item.id === cId && item.eventType === 'clip') as TimelineClip | undefined;
        if (c) {
          setClipStartInput(formatSeconds(c.startTime));
          setClipEndInput(formatSeconds(c.endTime));
          setClipTitleInput(c.title || '');
          setClipNotesInput(c.notes || '');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimelineItem, selectedKey, periods, game.goals, timelineEvents, periodDrafts, clipDrafts, goalDrafts]);

  const goals = game.goals || [];
  const jetsGoalCount = goals.filter((g) => isGoalByJets(g, isJetsHome, game?.targetTeam)).length;
  const oppGoalCount = goals.length - jetsGoalCount;

  // Selected Item references
  const selectedPeriodObj = selectedTimelineItem?.type === 'period'
    ? periods.find((p) => p.period === selectedTimelineItem.periodNumber)
    : null;

  const selectedGoalObj = selectedTimelineItem?.type === 'goal'
    ? (findGoalBySelection(goals, selectedTimelineItem) || null)
    : null;

  const isPeriodUnlocked = Boolean(
    isItemUnlocked('period', selectedPeriodObj?.period ?? -1) ||
    selectedPeriodObj?.isLocked === false
  );

  const isGoalUnlocked = Boolean(
    selectedGoalObj && (
      isItemUnlocked('goal', getGoalTargetId(selectedGoalObj)) ||
      isItemUnlocked('goal', selectedGoalObj.goalNumber) ||
      selectedGoalObj.isLocked === false
    )
  );

  const selectedNoteObj = selectedTimelineItem?.type === 'note'
    ? ((timelineEvents.find((e) => e.id === selectedTimelineItem.id && e.eventType === 'note') as TimelineNote) || null)
    : null;

  const isNoteUnlocked = Boolean(
    isItemUnlocked('note', selectedNoteObj?.id ?? '') ||
    selectedNoteObj?.isLocked === false
  );

  const selectedClipObj = selectedTimelineItem?.type === 'clip'
    ? ((timelineEvents.find((e) => e.id === selectedTimelineItem.id && e.eventType === 'clip') as TimelineClip) || null)
    : null;

  const isClipUnlocked = Boolean(
    isItemUnlocked('clip', selectedClipObj?.id ?? '') ||
    selectedClipObj?.isLocked === false
  );

  // Sync period form inputs when period is dragged/modified on timeline while input is not focused and user has not drafted changes
  useEffect(() => {
    if (selectedTimelineItem?.type === 'period' && selectedPeriodObj) {
      const hasDraft = Boolean(periodDrafts[selectedPeriodObj.period]);
      if (!isPeriodStartFocusedRef.current && !hasDraft) {
        setPeriodStartInput(formatSeconds(selectedPeriodObj.startTime));
      }
      if (!isPeriodEndFocusedRef.current && !hasDraft) {
        setPeriodEndInput(formatSeconds(selectedPeriodObj.endTime));
      }
    }
  }, [selectedTimelineItem, selectedPeriodObj, periodDrafts]);

  // Sync goal form inputs when goal is dragged/modified on timeline while input is not focused and user has not drafted changes
  useEffect(() => {
    if (selectedTimelineItem?.type === 'goal' && selectedGoalObj) {
      const gh = findGoalHighlight(selectedGoalObj, game.highlights || []);
      const startSec = selectedGoalObj.clipStartSeconds !== undefined
        ? selectedGoalObj.clipStartSeconds
        : (gh?.clipStartSeconds !== undefined ? gh.clipStartSeconds : Math.max(0, selectedGoalObj.videoTimestamp - 15));
      const endSec = selectedGoalObj.clipEndSeconds !== undefined
        ? selectedGoalObj.clipEndSeconds
        : (gh?.clipEndSeconds !== undefined ? gh.clipEndSeconds : selectedGoalObj.videoTimestamp + 5);

      const draftKey = getGoalDraftKey(selectedGoalObj);
      const hasDraft = Boolean(goalDrafts[draftKey] || goalDrafts[String(selectedGoalObj.goalNumber)]);

      if (!isGoalClipStartFocusedRef.current && !hasDraft) {
        setGoalClipStartInput(formatSeconds(startSec));
      }
      if (!isGoalClipEndFocusedRef.current && !hasDraft) {
        setGoalClipEndInput(formatSeconds(endSec));
      }
      if (!isGoalTimeFocusedRef.current && !hasDraft) {
        setGoalTimeInput(formatSeconds(selectedGoalObj.videoTimestamp));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimelineItem, selectedGoalObj, game.highlights, goalDrafts]);

  // Sync note form input when dragged on timeline while not focused and user has not drafted changes
  useEffect(() => {
    if (selectedTimelineItem?.type === 'note' && selectedNoteObj) {
      const isDirty = noteSnapshot !== null && noteTimeInput !== noteSnapshot.time;
      if (!isNoteTimeFocusedRef.current && !isDirty) {
        setNoteTimeInput(formatSeconds(selectedNoteObj.time));
      }
    }
  }, [selectedTimelineItem, selectedNoteObj, noteSnapshot, noteTimeInput]);

  // Sync clip form inputs when clip is dragged/modified on timeline while input is not focused and user has not drafted changes
  useEffect(() => {
    if (selectedTimelineItem?.type === 'clip' && selectedClipObj) {
      const hasDraft = Boolean(clipDrafts[selectedClipObj.id]);
      if (!isClipStartFocusedRef.current && !hasDraft) {
        setClipStartInput(formatSeconds(selectedClipObj.startTime));
      }
      if (!isClipEndFocusedRef.current && !hasDraft) {
        setClipEndInput(formatSeconds(selectedClipObj.endTime));
      }
    }
  }, [selectedTimelineItem, selectedClipObj, clipDrafts]);

  const handleUnlockClip = () => {
    if (!selectedClipObj) return;
    setClipSnapshot({
      start: clipStartInput,
      end: clipEndInput,
      title: clipTitleInput,
      notes: clipNotesInput,
    });
    if (onUnlockItem) {
      onUnlockItem('clip', selectedClipObj.id);
    }
    setLocalUnlockedItem({ type: 'clip', id: selectedClipObj.id });
  };

  const handleDiscardClip = () => {
    setClipTypeInput('clip');
    if (clipSnapshot) {
      setClipStartInput(clipSnapshot.start);
      setClipEndInput(clipSnapshot.end);
      setClipTitleInput(clipSnapshot.title);
      setClipNotesInput(clipSnapshot.notes);
    }
    if (selectedClipObj) {
      setClipDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedClipObj.id];
        return next;
      });
    }
    if (onDiscardChanges) {
      onDiscardChanges();
    }
    setLocalUnlockedItem(null);
    setClipSnapshot(null);
  };

  const handleSaveClip = async () => {
    if (!selectedClipObj) return;
    if (clipTypeInput !== 'clip' && onConvertEventType) {
      await onConvertEventType('clip', selectedClipObj.id, clipTypeInput);
      setLocalUnlockedItem(null);
      setClipSnapshot(null);
      return;
    }
    if (!onUpdateClip) return;
    setSavingClip(true);
    try {
      const s = parseTimeInput(clipStartInput);
      const e = parseTimeInput(clipEndInput);
      await onUpdateClip(selectedClipObj.id, {
        startTime: s,
        endTime: e,
        title: clipTitleInput,
        notes: clipNotesInput,
        isLocked: true,
      });
      if (onLockItem) {
        onLockItem('clip', selectedClipObj.id);
      }
      setLocalUnlockedItem(null);
      setClipSnapshot(null);
      setClipDrafts((prev) => {
        const next = { ...prev };
        if (selectedClipObj) delete next[selectedClipObj.id];
        return next;
      });
    } finally {
      setSavingClip(false);
    }
  };

  const handleUnlockNote = () => {
    if (!selectedNoteObj) return;
    setNoteSnapshot({ time: noteTimeInput, text: noteTextInput });
    if (onUnlockItem) {
      onUnlockItem('note', selectedNoteObj.id);
    }
    setLocalUnlockedItem({ type: 'note', id: selectedNoteObj.id });
  };

  const handleDiscardNote = () => {
    setNoteTypeInput('note');
    if (noteSnapshot) {
      setNoteTimeInput(noteSnapshot.time);
      setNoteTextInput(noteSnapshot.text);
      if (selectedNoteObj) {
        const parsed = parseTimeInput(noteSnapshot.time);
        if (!isNaN(parsed) && onUpdateNote) {
          // restore previous draft in memory
          onUpdateNote(selectedNoteObj.id, { time: parsed, text: noteSnapshot.text });
        }
      }
    }
    if (onDiscardChanges) {
      onDiscardChanges();
    }
    setLocalUnlockedItem(null);
    setNoteSnapshot(null);
  };

  const handleSaveNote = async () => {
    if (!selectedNoteObj) return;
    if (noteTypeInput !== 'note' && onConvertEventType) {
      await onConvertEventType('note', selectedNoteObj.id, noteTypeInput);
      setLocalUnlockedItem(null);
      setNoteSnapshot(null);
      return;
    }
    if (!onUpdateNote) return;
    setSavingNote(true);
    try {
      const t = parseTimeInput(noteTimeInput);
      await onUpdateNote(selectedNoteObj.id, {
        time: t,
        text: noteTextInput,
        isLocked: true,
      });
      if (onLockItem) {
        onLockItem('note', selectedNoteObj.id);
      }
      setLocalUnlockedItem(null);
      setNoteSnapshot(null);
    } finally {
      setSavingNote(false);
    }
  };

  const handleUnlockPeriod = () => {
    if (!selectedPeriodObj) return;
    setPeriodSnapshot({ start: periodStartInput, end: periodEndInput });
    if (onUnlockItem) {
      onUnlockItem('period', selectedPeriodObj.period);
    }
    setLocalUnlockedItem({ type: 'period', id: selectedPeriodObj.period });
  };

  const handlePeriodStartChange = (val: string) => {
    setPeriodStartInput(val);
    if (selectedPeriodObj) {
      setPeriodDrafts((prev) => ({
        ...prev,
        [selectedPeriodObj.period]: { start: val, end: periodEndInput },
      }));
      if (onUpdatePeriodDraft) {
        const s = parseTimeInput(val);
        const e = parseTimeInput(periodEndInput);
        if (!isNaN(s) && !isNaN(e)) {
          onUpdatePeriodDraft(selectedPeriodObj.period, s, e);
        }
      }
    }
  };

  const handlePeriodEndChange = (val: string) => {
    setPeriodEndInput(val);
    if (selectedPeriodObj) {
      setPeriodDrafts((prev) => ({
        ...prev,
        [selectedPeriodObj.period]: { start: periodStartInput, end: val },
      }));
      if (onUpdatePeriodDraft) {
        const s = parseTimeInput(periodStartInput);
        const e = parseTimeInput(val);
        if (!isNaN(s) && !isNaN(e)) {
          onUpdatePeriodDraft(selectedPeriodObj.period, s, e);
        }
      }
    }
  };

  const handleDiscardPeriod = () => {
    if (periodSnapshot) {
      setPeriodStartInput(periodSnapshot.start);
      setPeriodEndInput(periodSnapshot.end);
    }
    if (onDiscardChanges) {
      onDiscardChanges();
    }
    if (selectedPeriodObj) {
      if (onLockItem) {
        onLockItem('period', selectedPeriodObj.period);
      }
      setPeriodDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedPeriodObj.period];
        return next;
      });
    }
    setLocalUnlockedItem(null);
    setPeriodSnapshot(null);
  };

  const handleSavePeriod = async () => {
    if (!selectedPeriodObj || !onUpdatePeriod) return;
    setSavingPeriod(true);
    try {
      const s = parseTimeInput(periodStartInput);
      const e = parseTimeInput(periodEndInput);
      await onUpdatePeriod(selectedPeriodObj.period, s, e);
      if (onLockItem) {
        onLockItem('period', selectedPeriodObj.period);
      }
      setLocalUnlockedItem(null);
      setPeriodSnapshot(null);
      setPeriodDrafts((prev) => {
        const next = { ...prev };
        if (selectedPeriodObj) delete next[selectedPeriodObj.period];
        return next;
      });
    } finally {
      setSavingPeriod(false);
    }
  };

  const handleSaveAllClick = async () => {
    for (const [pNumStr, draft] of Object.entries(periodDrafts)) {
      const pNum = Number(pNumStr);
      const s = parseTimeInput(draft.start);
      const e = parseTimeInput(draft.end);
      if (onUpdatePeriodDraft && !isNaN(s) && !isNaN(e)) {
        onUpdatePeriodDraft(pNum, s, e);
      }
    }
    for (const [gKey, draft] of Object.entries(goalDrafts)) {
      const gTarget = isNaN(Number(gKey)) ? gKey : Number(gKey);
      const startSec = draft.clipStart !== undefined ? parseTimeInput(draft.clipStart) : NaN;
      const endSec = draft.clipEnd !== undefined ? parseTimeInput(draft.clipEnd) : NaN;
      const sec = draft.time !== undefined ? parseTimeInput(draft.time) : NaN;
      if (onUpdateGoalDraft) {
        const metaUpdates: Partial<GoalEventData> = {};
        if (draft.team !== undefined) metaUpdates.scoringTeam = draft.team;
        if (draft.period !== undefined) metaUpdates.period = Number(draft.period) || 1;
        if (draft.score !== undefined) metaUpdates.scoreAfter = draft.score;
        if (draft.clock !== undefined) metaUpdates.gameClock = draft.clock;
        if (draft.scorer !== undefined) metaUpdates.scorer = draft.scorer;
        if (draft.jersey !== undefined) metaUpdates.scorerJersey = draft.jersey;
        if (draft.assists !== undefined) {
          metaUpdates.assists = draft.assists ? draft.assists.split(',').map((a: string) => a.trim()).filter(Boolean) : [];
        }
        if (draft.desc !== undefined) metaUpdates.description = draft.desc;
        if (Object.keys(metaUpdates).length > 0) {
          onUpdateGoalDraft(
            gTarget,
            isNaN(startSec) ? undefined : startSec,
            isNaN(endSec) ? undefined : endSec,
            isNaN(sec) ? undefined : sec,
            metaUpdates
          );
        } else {
          onUpdateGoalDraft(
            gTarget,
            isNaN(startSec) ? undefined : startSec,
            isNaN(endSec) ? undefined : endSec,
            isNaN(sec) ? undefined : sec
          );
        }
      }
    }
    if (onSaveAll) {
      await onSaveAll();
    }
    setPeriodDrafts({});
    setClipDrafts({});
    setGoalDrafts({});
    setLocalUnlockedItem(null);
  };

  const handleDiscardAllClick = () => {
    if (onDiscardAll) {
      onDiscardAll();
    } else if (onDiscardChanges) {
      onDiscardChanges();
    }
    setPeriodDrafts({});
    setClipDrafts({});
    setGoalDrafts({});
    setLocalUnlockedItem(null);
  };

  const handleUnlockGoal = () => {
    if (!selectedGoalObj) return;
    const targetId = getGoalTargetId(selectedGoalObj);
    setGoalSnapshot({
      time: goalTimeInput,
      team: goalTeam,
      clipStart: goalClipStartInput,
      clipEnd: goalClipEndInput,
      period: goalPeriod,
      score: goalScore,
      clock: goalClock,
      scorer: goalScorer,
      jersey: goalJersey,
      assists: goalAssists,
      desc: goalDesc,
    });
    if (onUnlockItem) {
      onUnlockItem('goal', targetId);
    }
    setLocalUnlockedItem({ type: 'goal', id: targetId });
  };

  const handleDiscardGoal = () => {
    setGoalTypeInput('goal');
    if (goalSnapshot) {
      setGoalTimeInput(goalSnapshot.time);
      setGoalTeam(goalSnapshot.team);
      setGoalClipStartInput(goalSnapshot.clipStart);
      setGoalClipEndInput(goalSnapshot.clipEnd);
      setGoalPeriod(goalSnapshot.period);
      setGoalScore(goalSnapshot.score);
      setGoalClock(goalSnapshot.clock);
      setGoalScorer(goalSnapshot.scorer);
      setGoalJersey(goalSnapshot.jersey);
      setGoalAssists(goalSnapshot.assists);
      setGoalDesc(goalSnapshot.desc);
      if (selectedGoalObj && onUpdateGoalDraft) {
        const targetId = getGoalTargetId(selectedGoalObj);
        const startSec = parseTimeInput(goalSnapshot.clipStart);
        const endSec = parseTimeInput(goalSnapshot.clipEnd);
        const sec = parseTimeInput(goalSnapshot.time);
        onUpdateGoalDraft(
          targetId,
          isNaN(startSec) ? undefined : startSec,
          isNaN(endSec) ? undefined : endSec,
          isNaN(sec) ? undefined : sec
        );
      }
    }
    if (onLockItem && selectedGoalObj) {
      onLockItem('goal', getGoalTargetId(selectedGoalObj));
    }
    if (onDiscardChanges) {
      onDiscardChanges();
    }
    setLocalUnlockedItem(null);
    setGoalSnapshot(null);
    setGoalDrafts((prev) => {
      const next = { ...prev };
      if (selectedGoalObj) {
        delete next[getGoalDraftKey(selectedGoalObj)];
        delete next[String(selectedGoalObj.goalNumber)];
      }
      return next;
    });
  };

  const handleSaveGoal = async () => {
    if (!selectedGoalObj) return;
    const targetId = getGoalTargetId(selectedGoalObj);
    if (goalTypeInput !== 'goal' && onConvertEventType) {
      await onConvertEventType('goal', targetId, goalTypeInput);
      setLocalUnlockedItem(null);
      setGoalSnapshot(null);
      return;
    }
    if (!onUpdateGoal) return;
    setSavingGoal(true);
    try {
      const clipStart = parseTimeInput(goalClipStartInput);
      const clipEnd = parseTimeInput(goalClipEndInput);
      const parsedTime = parseTimeInput(goalTimeInput);
      const assistsList = goalAssists ? goalAssists.split(',').map((a) => a.trim()).filter(Boolean) : [];
      let finalTs = selectedGoalObj.videoTimestamp || 0;
      if (!isNaN(parsedTime) && goalTimeInput !== formatSeconds(selectedGoalObj.videoTimestamp)) {
        finalTs = parsedTime;
      }
      if (!isNaN(clipStart) && !isNaN(clipEnd)) {
        if (finalTs < clipStart || finalTs > clipEnd) {
          finalTs = Math.round(Math.max(clipStart, clipEnd - Math.min(3, (clipEnd - clipStart) * 0.25)) * 10) / 10;
        }
      } else if (!isNaN(clipStart)) {
        finalTs = Math.max(clipStart, finalTs);
      }
      await onUpdateGoal(targetId, {
        videoTimestamp: finalTs,
        scoringTeam: goalTeam,
        clipStartSeconds: isNaN(clipStart) ? undefined : clipStart,
        clipEndSeconds: isNaN(clipEnd) ? undefined : clipEnd,
        durationSeconds: (!isNaN(clipStart) && !isNaN(clipEnd)) ? Math.max(0, clipEnd - clipStart) : undefined,
        period: Number(goalPeriod) || 1,
        scoreAfter: goalScore,
        gameClock: goalClock,
        scorer: goalScorer,
        scorerJersey: goalJersey,
        assists: assistsList,
        description: goalDesc,
        isModified: true,
        isLocked: true,
      });
      if (onLockItem) {
        onLockItem('goal', targetId);
      }
      setLocalUnlockedItem(null);
      setGoalSnapshot(null);
      setGoalDrafts((prev) => {
        const next = { ...prev };
        if (selectedGoalObj) {
          delete next[getGoalDraftKey(selectedGoalObj)];
          delete next[String(selectedGoalObj.goalNumber)];
        }
        return next;
      });
    } finally {
      setSavingGoal(false);
    }
  };

  if (isSidebarCollapsed) {
    return null;
  }

  return (
    <Box
      sx={{
        width: sidebarWidth,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#141416',
        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          bgcolor: '#18181b',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            {focalTeamFull} vs {game.opponent || 'Opponent'}
          </Typography>
          <Tooltip title="Collapse sidebar">
            <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>
          {game.date} • {game.scheduledStart} • {game.rinkName} {game.sheetName && `(${game.sheetName})`}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Tabs
        value={sidebarTab}
        onChange={(_, val) => setSidebarTab(val)}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          bgcolor: '#18181b',
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0.5,
            px: 0.75,
            fontSize: '0.72rem',
            textTransform: 'none',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': { color: 'primary.light' },
          },
        }}
      >
        <Tab
          icon={<EventNoteIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
          label={`Events (${goals.length + timelineEvents.length + highlights.length})`}
        />
        <Tab
          icon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
          label="Info & Feeds"
        />
        <Tab
          icon={<TuneIcon sx={{ fontSize: 15 }} />}
          iconPosition="start"
          label={selectedTimelineItem ? 'Inspector ●' : 'Inspector'}
        />
      </Tabs>

      {/* Tab Contents */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5, scrollbarWidth: 'thin' }}>
        {/* Tab 0: Unified Events Tab */}
        {sidebarTab === 0 && (() => {
          const customClips = timelineEvents.filter((e) => e.eventType === 'clip') as TimelineClip[];
          const customNotes = timelineEvents.filter((e) => e.eventType === 'note') as TimelineNote[];
          const totalClipsCount = customClips.length + highlights.length;
          const totalEventsCount = goals.length + customClips.length + highlights.length + customNotes.length;

          type DisplayEvent =
            | { type: 'goal'; time: number; goal: GoalEventData; idx: number }
            | { type: 'clip'; time: number; clip: TimelineClip }
            | { type: 'highlight'; time: number; highlight: GoalHighlight; idx: number }
            | { type: 'note'; time: number; note: TimelineNote };

          const displayEvents: DisplayEvent[] = [];

          if (eventFilter === 'all' || eventFilter === 'goals') {
            goals.forEach((g, idx) => {
              const isJets = isGoalByJets(g, isJetsHome, game?.targetTeam);
              if (eventFilter === 'goals') {
                if (goalFilter === 'jets' && !isJets) return;
                if (goalFilter === 'opponent' && isJets) return;
              }
              displayEvents.push({ type: 'goal', time: g.videoTimestamp || 0, goal: g, idx });
            });
          }

          if (eventFilter === 'all' || eventFilter === 'clips') {
            customClips.forEach((c) => {
              displayEvents.push({ type: 'clip', time: c.startTime || 0, clip: c });
            });
            highlights.forEach((hl, idx) => {
              displayEvents.push({ type: 'highlight', time: hl.clipStartSeconds || 0, highlight: hl, idx });
            });
          }

          if (eventFilter === 'all' || eventFilter === 'notes') {
            customNotes.forEach((n) => {
              displayEvents.push({ type: 'note', time: n.time || 0, note: n });
            });
          }

          displayEvents.sort((a, b) => a.time - b.time);

          return (
            <Stack spacing={1.5}>
              {/* Header controls & filter */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                  value={eventFilter}
                  exclusive
                  size="small"
                  onChange={(_, val) => val && setEventFilter(val)}
                  sx={{
                    height: 28,
                    '& .MuiToggleButton-root': {
                      px: 1,
                      py: 0.25,
                      fontSize: '0.7rem',
                      color: 'rgba(255, 255, 255, 0.75)',
                      '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff' },
                    },
                  }}
                >
                  <ToggleButton value="all">All ({totalEventsCount})</ToggleButton>
                  <ToggleButton value="goals">Goals ({goals.length})</ToggleButton>
                  <ToggleButton value="clips">Clips ({totalClipsCount})</ToggleButton>
                  <ToggleButton value="notes">Notes ({customNotes.length})</ToggleButton>
                </ToggleButtonGroup>

                <Stack direction="row" spacing={0.5}>
                  {canManageEvents && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => onOpenAddEvent('goal')}
                      sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                    >
                      Add Marker
                    </Button>
                  )}
                  {highlights.length > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<MovieFilterIcon />}
                      onClick={() => onPlayHighlightClip(0)}
                      sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8 }}
                    >
                      Play All
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Sub-filter for goals when goals filter is active */}
              {eventFilter === 'goals' && goals.length > 0 && (
                <ToggleButtonGroup
                  value={goalFilter}
                  exclusive
                  size="small"
                  onChange={(_, val) => val && setGoalFilter(val)}
                  sx={{
                    height: 24,
                    '& .MuiToggleButton-root': {
                      px: 0.75,
                      py: 0.1,
                      fontSize: '0.65rem',
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' },
                    },
                  }}
                >
                  <ToggleButton value="all">All Goals ({goals.length})</ToggleButton>
                  <ToggleButton value="jets">{focalTeamShort} ({jetsGoalCount})</ToggleButton>
                  <ToggleButton value="opponent">Opp ({oppGoalCount})</ToggleButton>
                </ToggleButtonGroup>
              )}

              {/* Events list */}
              {displayEvents.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic', mb: 1 }}>
                    {eventFilter === 'goals'
                      ? 'No goals recorded or detected in this broadcast.'
                      : eventFilter === 'clips'
                      ? 'No clips available for this game yet.'
                      : eventFilter === 'notes'
                      ? 'No notes recorded for this game yet.'
                      : 'No events recorded or detected in this broadcast.'}
                  </Typography>
                  {canManageEvents && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<BookmarkAddIcon />}
                      onClick={() => onOpenAddEvent(eventFilter === 'clips' ? 'clip' : 'goal')}
                      sx={{ mt: 1, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                    >
                      Record Event at Playhead
                    </Button>
                  )}
                </Box>
              ) : (
                <Stack spacing={1}>
                  {displayEvents.map((item) => {
                    if (item.type === 'goal') {
                      const g = item.goal;
                      const isJets = isGoalByJets(g, isJetsHome, game?.targetTeam);
                      const teamLabel = getGoalTeamName(g, isJetsHome, game?.opponent, game?.targetTeam);
                      const companionHighlight = findGoalHighlight(g, highlights);
                      const provenance = getGoalProvenanceInfo(g);
                      const goalEventId = g.eventId || g.id;

                      return (
                        <Paper
                          key={`goal-${goalEventId || g.goalNumber}-${item.idx}`}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            cursor: 'pointer',
                            borderRadius: 1.5,
                            bgcolor: '#1c1c1f',
                            borderColor: 'rgba(255,255,255,0.1)',
                            color: '#ffffff',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'primary.main' },
                          }}
                          onClick={() => {
                            onJumpToGoal(g.videoTimestamp);
                            if (onSelectItem) {
                              onSelectItem({
                                type: 'goal',
                                goalNumber: g.goalNumber,
                                ...(goalEventId ? { eventId: goalEventId } : {}),
                              });
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip
                                label={teamLabel}
                                size="small"
                                color={isJets ? 'primary' : 'default'}
                                variant={isJets ? 'filled' : 'outlined'}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  ...(!isJets && {
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    borderColor: 'rgba(255, 255, 255, 0.35)',
                                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                                  }),
                                }}
                              />
                              <Typography variant="body2" fontWeight={700} sx={{ color: '#ffffff' }}>
                                Goal #{g.goalNumber}
                                {g.scoreAfter && (
                                  <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 400, ml: 0.5 }}>
                                    ({g.scoreAfter})
                                  </Box>
                                )}
                              </Typography>
                              <Tooltip title={provenance.tooltip}>
                                <Chip
                                  label={provenance.label}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.62rem',
                                    color: provenance.isAuto
                                      ? (provenance.isModified ? '#fbbf24' : '#38bdf8')
                                      : 'rgba(255, 255, 255, 0.6)',
                                    borderColor: provenance.isAuto
                                      ? (provenance.isModified ? 'rgba(251, 191, 36, 0.4)' : 'rgba(56, 189, 248, 0.4)')
                                      : 'rgba(255, 255, 255, 0.2)',
                                  }}
                                />
                              </Tooltip>
                            </Stack>

                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.light', fontWeight: 600 }}>
                                {g.videoTimestampStr || formatSeconds(g.videoTimestamp)}
                              </Typography>
                              {canManageEvents && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteEvent(goalEventId || `goal-${g.goalNumber}`);
                                  }}
                                  disabled={deletingEventId === (goalEventId || `goal-${g.goalNumber}`)}
                                  sx={{ p: 0.25 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </Stack>
                          </Box>

                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', display: 'block', mb: 1 }}>
                            Period {g.period} {g.gameClock && `• ${g.gameClock}`}
                            {g.description && ` — ${g.description}`}
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PlayArrowIcon />}
                                sx={{ fontSize: '0.7rem', py: 0.2, px: 0.8, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onJumpToGoal(g.videoTimestamp);
                                }}
                              >
                                Jump
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<TuneIcon sx={{ fontSize: 13 }} />}
                                sx={{ fontSize: '0.7rem', py: 0.2, px: 0.8, color: '#38bdf8' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onSelectItem) {
                                    onSelectItem({
                                      type: 'goal',
                                      goalNumber: g.goalNumber,
                                      ...(goalEventId ? { eventId: goalEventId } : {}),
                                    });
                                  }
                                  setSidebarTab(2);
                                }}
                              >
                                Inspect
                              </Button>
                            </Stack>

                            {companionHighlight && onDownloadHighlightClip ? (
                              <Tooltip title="Download goal clip">
                                <IconButton
                                  aria-label="Download clip"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDownloadHighlightClip(companionHighlight);
                                  }}
                                  sx={{ p: 0.4, color: 'primary.light' }}
                                >
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : onDownloadClipPrompt ? (
                              <Tooltip title="Download goal clip">
                                <IconButton
                                  aria-label="Download clip"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const startSec = g.clipStartSeconds !== undefined
                                      ? g.clipStartSeconds
                                      : (companionHighlight?.clipStartSeconds !== undefined ? companionHighlight.clipStartSeconds : Math.max(0, g.videoTimestamp - 15));
                                    const endSec = g.clipEndSeconds !== undefined
                                      ? g.clipEndSeconds
                                      : (companionHighlight?.clipEndSeconds !== undefined ? companionHighlight.clipEndSeconds : g.videoTimestamp + 5);
                                    const goalAsClip: TimelineClip = {
                                      id: g.eventId || `goal-${g.goalNumber}`,
                                      title: `Goal #${g.goalNumber}: ${teamLabel}`,
                                      startTime: startSec,
                                      endTime: endSec,
                                      duration: Math.max(0, endSec - startSec),
                                      notes: g.description || '',
                                      eventType: 'clip',
                                    };
                                    onDownloadClipPrompt(goalAsClip);
                                  }}
                                  sx={{ p: 0.4, color: 'primary.light' }}
                                >
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Paper>
                      );
                    }

                    if (item.type === 'clip') {
                      const c = item.clip;
                      const dur = c.endTime - c.startTime;
                      return (
                        <Paper
                          key={`custom-clip-${c.id}`}
                          variant="outlined"
                          onClick={() => {
                            if (onJumpToTime) onJumpToTime(c.startTime);
                            if (onSelectItem) onSelectItem({ type: 'clip', id: c.id });
                          }}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: '#1c1c1f',
                            borderColor: 'rgba(56, 189, 248, 0.4)',
                            color: '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#242429', borderColor: '#38bdf8' },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                icon={<ContentCutIcon sx={{ fontSize: '13px !important' }} />}
                                label="Clip"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', borderColor: 'rgba(56, 189, 248, 0.4)', border: '1px solid' }}
                              />
                              <Typography variant="subtitle2" fontWeight={700}>
                                {c.title || 'Custom Clip'}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#38bdf8' }}>
                              {dur > 0 ? `${dur.toFixed(1)}s` : ''}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                            {formatSeconds(c.startTime)} → {formatSeconds(c.endTime)} {c.notes && `• ${c.notes}`}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PlayArrowIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onJumpToTime) onJumpToTime(c.startTime);
                                }}
                                sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                              >
                                Play
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<TuneIcon sx={{ fontSize: 13 }} />}
                                sx={{ fontSize: '0.7rem', py: 0.2, px: 0.8, color: '#38bdf8' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onSelectItem) onSelectItem({ type: 'clip', id: c.id });
                                  setSidebarTab(2);
                                }}
                              >
                                Inspect
                              </Button>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {onDownloadClipPrompt && (
                                <Tooltip title="Download clip">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDownloadClipPrompt(c);
                                    }}
                                    sx={{ color: '#f59e0b', p: 0.4 }}
                                  >
                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {onOpenShareModal && (
                                <Tooltip title="Share clip">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenShareModal(c);
                                    }}
                                    sx={{ color: '#38bdf8', p: 0.4 }}
                                  >
                                    <ShareIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {onDeleteTimelineEvent && canManageEvents && (
                                <Tooltip title="Delete clip">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteTimelineEvent(c.id);
                                    }}
                                    sx={{ p: 0.4 }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                        </Paper>
                      );
                    }

                    if (item.type === 'highlight') {
                      const hl = item.highlight;
                      return (
                        <Paper
                          key={`highlight-${hl.goalNumber || item.idx}`}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: '#1c1c1f',
                            borderColor: 'rgba(255,255,255,0.1)',
                            color: '#ffffff',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {hl.title || hl.playDescription || `${hl.scoringTeam ? hl.scoringTeam + ' ' : ''}Goal #${hl.goalNumber || item.idx + 1}`}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.light' }}>
                              {hl.durationSeconds ? `${hl.durationSeconds.toFixed(1)}s` : ''}
                            </Typography>
                          </Box>

                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                            {formatSeconds(hl.clipStartSeconds || 0)} → {formatSeconds(hl.clipEndSeconds || 0)}
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => onPlayHighlightClip(item.idx)}
                              sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                            >
                              Play
                            </Button>
                            {onDownloadHighlightClip && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<DownloadIcon />}
                                onClick={() => onDownloadHighlightClip(hl)}
                                sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8, color: 'primary.light' }}
                              >
                                Save
                              </Button>
                            )}
                          </Stack>
                        </Paper>
                      );
                    }

                    if (item.type === 'note') {
                      const n = item.note;
                      return (
                        <Paper
                          key={`note-${n.id}`}
                          variant="outlined"
                          onClick={() => {
                            if (onJumpToTime) onJumpToTime(n.time);
                            else onJumpToGoal(n.time);
                            if (onSelectItem) onSelectItem({ type: 'note', id: n.id });
                          }}
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: '#1c1c1f',
                            borderColor: 'rgba(245, 158, 11, 0.4)',
                            color: '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#242429', borderColor: '#f59e0b' },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                icon={<StickyNote2Icon sx={{ fontSize: '13px !important' }} />}
                                label="Note"
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', borderColor: 'rgba(245, 158, 11, 0.4)', border: '1px solid' }}
                              />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#f59e0b', fontWeight: 600 }}>
                                {formatSeconds(n.time)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {onOpenShareModal && (
                                <Tooltip title="Share note">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onOpenShareModal(n);
                                    }}
                                    sx={{ color: '#38bdf8', p: 0.4 }}
                                  >
                                    <ShareIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {onDeleteTimelineEvent && canManageEvents && (
                                <Tooltip title="Delete note">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteTimelineEvent(n.id);
                                    }}
                                    sx={{ p: 0.4 }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', mb: 1, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {n.text || '(Empty Note)'}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PlayArrowIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onJumpToTime) onJumpToTime(n.time);
                                  else onJumpToGoal(n.time);
                                }}
                                sx={{ fontSize: '0.72rem', py: 0.2, px: 0.8, color: '#fff', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                              >
                                Jump
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<TuneIcon sx={{ fontSize: 13 }} />}
                                sx={{ fontSize: '0.7rem', py: 0.2, px: 0.8, color: '#f59e0b' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onSelectItem) onSelectItem({ type: 'note', id: n.id });
                                  setSidebarTab(2);
                                }}
                              >
                                Inspect
                              </Button>
                            </Stack>
                            {onDownloadClipPrompt && (
                              <Tooltip title="Download note as clip">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const noteClip: TimelineClip = {
                                      id: n.id,
                                      title: `Note @ ${formatSeconds(n.time)}: ${n.text.slice(0, 30) || 'Note'}`,
                                      startTime: Math.max(0, n.time - 10),
                                      endTime: n.time + 10,
                                      duration: 20,
                                      notes: n.text,
                                      eventType: 'clip',
                                    };
                                    onDownloadClipPrompt(noteClip);
                                  }}
                                  sx={{ color: '#f59e0b', p: 0.4 }}
                                >
                                  <DownloadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Paper>
                      );
                    }

                    return null;
                  })}
                </Stack>
              )}
            </Stack>
          );
        })()}

        {/* Tab 1: Feeds & Info */}
        {sidebarTab === 1 && (
          <Stack spacing={2}>
            {/* Timeline Milestones */}
            {game.timeline && (
              <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: '#1c1c1f', borderColor: 'rgba(255,255,255,0.1)', color: '#ffffff' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#ffffff' }}>
                    <ScheduleIcon color="primary" fontSize="small" />
                    Broadcast Timeline
                  </Typography>

                  <Stack spacing={0.75}>
                    {game.timeline.puckDrop && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Puck Drop / Period 1:</Typography>
                        <Button size="small" variant="text" sx={{ py: 0, px: 0.5, fontSize: '0.72rem', color: 'primary.light' }} onClick={() => onJumpToPeriod(1)}>
                          {game.timeline.puckDrop}
                        </Button>
                      </Box>
                    )}
                    {game.timeline.period2Start && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Period 2 Start:</Typography>
                        <Button size="small" variant="text" sx={{ py: 0, px: 0.5, fontSize: '0.72rem', color: 'primary.light' }} onClick={() => onJumpToPeriod(2)}>
                          {game.timeline.period2Start}
                        </Button>
                      </Box>
                    )}
                    {game.timeline.period3Start && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Period 3 Start:</Typography>
                        <Button size="small" variant="text" sx={{ py: 0, px: 0.5, fontSize: '0.72rem', color: 'primary.light' }} onClick={() => onJumpToPeriod(3)}>
                          {game.timeline.period3Start}
                        </Button>
                      </Box>
                    )}
                    {game.timeline.overtimeStart && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Overtime Start:</Typography>
                        <Button size="small" variant="text" sx={{ py: 0, px: 0.5, fontSize: '0.72rem', color: 'primary.light' }} onClick={() => onJumpToPeriod(4)}>
                          {game.timeline.overtimeStart}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Audit Trail / Edit History Card */}
            {editHistory.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: '#1c1c1f', borderColor: 'rgba(255,255,255,0.1)', color: '#ffffff' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#ffffff' }}>
                    <HistoryIcon color="primary" fontSize="small" />
                    Timeline Edit History ({editHistory.length})
                  </Typography>

                  <Stack spacing={0.75} sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {editHistory.slice().reverse().map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            label={entry.action.replace('_', ' ')}
                            size="small"
                            sx={{ height: 16, fontSize: '0.6rem', textTransform: 'uppercase' }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)' }}>
                            {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mt: 0.25 }}>
                          {entry.details}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>
                          by {entry.userEmail}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Feeds Status Card */}
            <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: '#1c1c1f', borderColor: 'rgba(255,255,255,0.1)', color: '#ffffff' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#ffffff' }}>
                  <InfoOutlinedIcon color="primary" fontSize="small" />
                  LiveBarn Video Feeds
                </Typography>

                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Auto-Tracking:</Typography>
                    <Chip
                      label={game.feeds?.auto?.status === 'downloaded' ? 'Ready' : (game.feeds?.auto?.status || 'None')}
                      size="small"
                      color={game.feeds?.auto?.status === 'downloaded' ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)' }}>Panoramic:</Typography>
                    <Chip
                      label={game.feeds?.pano?.status === 'downloaded' ? 'Ready' : (game.feeds?.pano?.status === 'pending' ? 'Scheduled Retry' : (game.feeds?.pano?.status || 'None'))}
                      size="small"
                      color={game.feeds?.pano?.status === 'downloaded' ? 'success' : (game.feeds?.pano?.status === 'pending' ? 'warning' : 'default')}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Stack>

                {canManageEvents && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<TerminalIcon />}
                      onClick={onOpenLogsModal}
                      sx={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.85)', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                    >
                      Debug Logs
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}

        {/* Tab 2: Dedicated Inspector Tab */}
        {sidebarTab === 2 && (
          <Stack spacing={2}>
            {/* Multi-Item Save All / Lock All Banner */}
            {unlockedItemsList.length > 1 && (
              <Paper
                data-testid="multi-edit-save-all-banner"
                sx={{
                  p: 1.25,
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LockOpenIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                  <Typography variant="body2" sx={{ color: '#fcd34d', fontWeight: 600, fontSize: '0.8rem' }}>
                    {unlockedItemsList.length} items unlocked
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {onDiscardAll && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleDiscardAllClick}
                      sx={{
                        fontSize: '0.72rem',
                        py: 0.2,
                        px: 1,
                        color: 'rgba(255, 255, 255, 0.8)',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        textTransform: 'none',
                        '&:hover': { borderColor: '#fff' },
                      }}
                    >
                      Discard All
                    </Button>
                  )}
                  {onSaveAll && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveAllClick}
                      disabled={savingAll}
                      startIcon={<LockIcon sx={{ fontSize: '13px !important' }} />}
                      sx={{
                        fontSize: '0.72rem',
                        py: 0.2,
                        px: 1.25,
                        bgcolor: '#f59e0b',
                        color: '#000',
                        fontWeight: 700,
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#d97706' },
                      }}
                    >
                      Save All / Lock All
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}

            {!selectedTimelineItem && (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <TuneIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 0.5 }}>
                  No Timeline Item Selected
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                  Click on any Period region or Goal marker in the timeline below to inspect and edit its details.
                </Typography>
              </Box>
            )}

            {/* PERIOD PROPERTY INSPECTOR */}
            {selectedTimelineItem?.type === 'period' && selectedPeriodObj && (
              <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: '#1c1c1f', borderColor: isPeriodUnlocked ? '#f59e0b' : '#38bdf8', color: '#fff' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip label={`Period ${selectedPeriodObj.period}`} color={isPeriodUnlocked ? "warning" : "primary"} size="small" sx={{ fontWeight: 700 }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {selectedPeriodObj.label}
                      </Typography>
                      <Chip
                        icon={isPeriodUnlocked ? <LockOpenIcon sx={{ fontSize: '13px !important' }} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                        label={isPeriodUnlocked ? "Unlocked (Editing)" : "Period Locked"}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          borderColor: isPeriodUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                          color: isPeriodUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {canManageEvents && !isPeriodUnlocked && (
                        <Tooltip title="Unlock period to edit start and end times">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                              onClick={handleUnlockPeriod}
                              aria-label="Unlock to Edit"
                              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.9, whiteSpace: 'nowrap' }}
                            >
                              Unlock to Edit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canManageEvents && isPeriodUnlocked && (
                        <>
                          <Tooltip title="Save period changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                aria-label="Save Period Changes"
                                startIcon={savingPeriod ? <CircularProgress size={13} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingPeriod}
                                onClick={handleSavePeriod}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                              >
                                Save
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Discard unsaved changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                aria-label="Discard Period Changes"
                                startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingPeriod}
                                onClick={handleDiscardPeriod}
                                sx={{ textTransform: 'none', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                              >
                                Discard
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {canManageEvents && onDeletePeriod && (
                        <Tooltip title="Delete this period">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              aria-label="Delete Period"
                              startIcon={<DeleteIcon sx={{ fontSize: '13px !important' }} />}
                              onClick={() => onDeletePeriod(selectedPeriodObj.period)}
                              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                            >
                              Delete
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {onDeselectItem && (
                        <Tooltip title="Close inspector">
                          <IconButton size="small" onClick={onDeselectItem} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 2 }}>
                    Duration: <strong>{formatSeconds(selectedPeriodObj.endTime - selectedPeriodObj.startTime)}</strong> ({(selectedPeriodObj.endTime - selectedPeriodObj.startTime).toFixed(1)}s)
                  </Typography>

                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Start Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={periodStartInput}
                          onChange={(e) => handlePeriodStartChange(e.target.value)}
                          onFocus={() => { isPeriodStartFocusedRef.current = true; }}
                          onBlur={() => { isPeriodStartFocusedRef.current = false; }}
                          disabled={!canManageEvents || !isPeriodUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        {canManageEvents && (
                          <Tooltip title={isPeriodUnlocked ? "Set start time to current playhead" : "Unlock to set start time to playhead"}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!isPeriodUnlocked}
                                onClick={() => handlePeriodStartChange(formatSeconds(currentTime))}
                                sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                              >
                                Playhead
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        End Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={periodEndInput}
                          onChange={(e) => handlePeriodEndChange(e.target.value)}
                          onFocus={() => { isPeriodEndFocusedRef.current = true; }}
                          onBlur={() => { isPeriodEndFocusedRef.current = false; }}
                          disabled={!canManageEvents || !isPeriodUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        {canManageEvents && (
                          <Tooltip title={isPeriodUnlocked ? "Set end time to current playhead" : "Unlock to set end time to playhead"}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!isPeriodUnlocked}
                                onClick={() => handlePeriodEndChange(formatSeconds(currentTime))}
                                sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                              >
                                Playhead
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => onJumpToPeriod(selectedPeriodObj.period)}
                      sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.75)' }}
                    >
                      Jump to Start of Period
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* GOAL PROPERTY INSPECTOR */}
            {selectedTimelineItem?.type === 'goal' && selectedGoalObj && (() => {
              const goalProvenance = getGoalProvenanceInfo(selectedGoalObj);
              const goalDebugId = selectedGoalObj.eventId || selectedGoalObj.id || `goal-${selectedGoalObj.goalNumber}`;
              const goalTargetId = getGoalTargetId(selectedGoalObj);
              const goalDraftKey = getGoalDraftKey(selectedGoalObj);
              return (
              <Card variant="outlined" sx={{ borderRadius: 1.5, bgcolor: '#1c1c1f', borderColor: isGoalUnlocked ? '#f59e0b' : '#fbbf24', color: '#fff' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip label={`Goal #${selectedGoalObj.goalNumber}`} sx={{ bgcolor: isGoalUnlocked ? '#f59e0b' : '#fbbf24', color: '#000', fontWeight: 700 }} size="small" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {getGoalTeamName(selectedGoalObj, isJetsHome, game?.opponent, game?.targetTeam)}
                      </Typography>
                      <Tooltip title={goalProvenance.tooltip}>
                        <Chip
                          data-testid="inspector-goal-provenance"
                          label={goalProvenance.label}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            color: goalProvenance.isAuto
                              ? (goalProvenance.isModified ? '#fbbf24' : '#38bdf8')
                              : 'rgba(255,255,255,0.65)',
                            borderColor: goalProvenance.isAuto
                              ? (goalProvenance.isModified ? 'rgba(251, 191, 36, 0.45)' : 'rgba(56, 189, 248, 0.45)')
                              : 'rgba(255,255,255,0.2)',
                          }}
                        />
                      </Tooltip>
                      <Chip
                        icon={isGoalUnlocked ? <LockOpenIcon sx={{ fontSize: '13px !important' }} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                        label={isGoalUnlocked ? "Unlocked (Editing)" : "Goal Locked"}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          borderColor: isGoalUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                          color: isGoalUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                        }}
                      />
                      <Tooltip title={`Event ID: ${goalDebugId} (click to copy)`}>
                        <Chip
                          data-testid="inspector-event-id"
                          label={`ID: ${goalDebugId}`}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard.writeText(String(goalDebugId)).catch(() => {});
                            }
                          }}
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            borderColor: 'rgba(255,255,255,0.14)',
                            color: 'rgba(255,255,255,0.45)',
                            '&:hover': {
                              color: 'rgba(255,255,255,0.8)',
                              borderColor: 'rgba(255,255,255,0.35)',
                            },
                          }}
                        />
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {canManageEvents && !isGoalUnlocked && (
                        <Tooltip title="Unlock goal to edit timestamp, period, or clock">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                              onClick={handleUnlockGoal}
                              aria-label="Unlock to Edit"
                              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.9, whiteSpace: 'nowrap' }}
                            >
                              Unlock to Edit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canManageEvents && isGoalUnlocked && (
                        <>
                          <Tooltip title="Save goal changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                aria-label="Save Goal"
                                startIcon={savingGoal ? <CircularProgress size={13} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingGoal}
                                onClick={handleSaveGoal}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                              >
                                Save
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Discard unsaved changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                aria-label="Discard Goal Changes"
                                startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingGoal}
                                onClick={handleDiscardGoal}
                                sx={{ textTransform: 'none', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                              >
                                Discard
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete goal event">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                aria-label="Delete"
                                startIcon={<DeleteIcon sx={{ fontSize: '13px !important' }} />}
                                onClick={() => onDeleteEvent(selectedGoalObj.eventId || selectedGoalObj.id || `goal-${selectedGoalObj.goalNumber}`)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                              >
                                Delete
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {onDeselectItem && (
                        <Tooltip title="Close inspector">
                          <IconButton size="small" onClick={onDeselectItem} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1.5 }}>
                    Duration: <strong>{formatSeconds(Math.max(0, parseTimeInput(goalClipEndInput) - parseTimeInput(goalClipStartInput)))}</strong> ({Math.max(0, parseTimeInput(goalClipEndInput) - parseTimeInput(goalClipStartInput)).toFixed(1)}s)
                  </Typography>

                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="goal-event-type-label">Event Type</InputLabel>
                      <Select
                        labelId="goal-event-type-label"
                        value={goalTypeInput}
                        label="Event Type"
                        disabled={!canManageEvents || !isGoalUnlocked}
                        onChange={(e) => setGoalTypeInput(e.target.value as any)}
                      >
                        <MenuItem value="goal">Goal</MenuItem>
                        <MenuItem value="clip">Clip / Highlight</MenuItem>
                        <MenuItem value="note">Note</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Scoring Team Selector */}
                    <FormControl size="small" fullWidth>
                      <InputLabel id="goal-scoring-team-label">Scoring Team</InputLabel>
                      <Select
                        labelId="goal-scoring-team-label"
                        value={goalTeam}
                        label="Scoring Team"
                        disabled={!canManageEvents || !isGoalUnlocked}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoalTeam(val);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              team: val,
                            },
                          }));
                        }}
                      >
                        <MenuItem value={focalTeamShort}>{focalTeamFull}</MenuItem>
                        <MenuItem value={game.opponent || 'Opponent'}>{game.opponent || 'Opponent'}</MenuItem>
                        {goalTeam !== focalTeamShort && goalTeam !== (game.opponent || 'Opponent') && (
                          <MenuItem value={goalTeam}>{goalTeam}</MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    {/* Start Time */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Start Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={goalClipStartInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setGoalClipStartInput(val);
                            const parsedStart = parseTimeInput(val);
                            const parsedEnd = parseTimeInput(goalClipEndInput);
                            if (!isNaN(parsedStart) && onUpdateGoalDraft) {
                              onUpdateGoalDraft(
                                goalTargetId,
                                parsedStart,
                                isNaN(parsedEnd) ? undefined : parsedEnd
                              );
                            }
                            setGoalDrafts((prev) => ({
                              ...prev,
                              [goalDraftKey]: {
                                ...(prev[goalDraftKey] || {}),
                                clipStart: val,
                              },
                            }));
                          }}
                          onFocus={() => { isGoalClipStartFocusedRef.current = true; }}
                          onBlur={() => { isGoalClipStartFocusedRef.current = false; }}
                          disabled={!canManageEvents || !isGoalUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        {canManageEvents && (
                          <Tooltip title={isGoalUnlocked ? "Set start time to current playhead" : "Unlock to set start time to playhead"}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!isGoalUnlocked}
                                onClick={() => {
                                  const s = formatSeconds(currentTime);
                                  setGoalClipStartInput(s);
                                  const parsedStart = Math.round(currentTime * 10) / 10;
                                  const parsedEnd = parseTimeInput(goalClipEndInput);
                                  if (onUpdateGoalDraft) {
                                    onUpdateGoalDraft(
                                      goalTargetId,
                                      parsedStart,
                                      isNaN(parsedEnd) ? undefined : parsedEnd
                                    );
                                  }
                                  setGoalDrafts((prev) => ({
                                    ...prev,
                                    [goalDraftKey]: {
                                      ...(prev[goalDraftKey] || {}),
                                      clipStart: s,
                                    },
                                  }));
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                              >
                                Playhead
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    {/* End Time */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        End Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={goalClipEndInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setGoalClipEndInput(val);
                            const parsedStart = parseTimeInput(goalClipStartInput);
                            const parsedEnd = parseTimeInput(val);
                            if (!isNaN(parsedEnd) && onUpdateGoalDraft) {
                              onUpdateGoalDraft(
                                goalTargetId,
                                isNaN(parsedStart) ? undefined : parsedStart,
                                parsedEnd
                              );
                            }
                            setGoalDrafts((prev) => ({
                              ...prev,
                              [goalDraftKey]: {
                                ...(prev[goalDraftKey] || {}),
                                clipEnd: val,
                              },
                            }));
                          }}
                          onFocus={() => { isGoalClipEndFocusedRef.current = true; }}
                          onBlur={() => { isGoalClipEndFocusedRef.current = false; }}
                          disabled={!canManageEvents || !isGoalUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        {canManageEvents && (
                          <Tooltip title={isGoalUnlocked ? "Set end time to current playhead" : "Unlock to set end time to playhead"}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!isGoalUnlocked}
                                onClick={() => {
                                  const e = formatSeconds(currentTime);
                                  setGoalClipEndInput(e);
                                  const parsedStart = parseTimeInput(goalClipStartInput);
                                  const parsedEnd = Math.round(currentTime * 10) / 10;
                                  if (onUpdateGoalDraft) {
                                    onUpdateGoalDraft(
                                      goalTargetId,
                                      isNaN(parsedStart) ? undefined : parsedStart,
                                      parsedEnd
                                    );
                                  }
                                  setGoalDrafts((prev) => ({
                                    ...prev,
                                    [goalDraftKey]: {
                                      ...(prev[goalDraftKey] || {}),
                                      clipEnd: e,
                                    },
                                  }));
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                              >
                                Playhead
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        label="Period"
                        type="number"
                        value={goalPeriod}
                        onChange={(e) => {
                          const pVal = parseInt(e.target.value, 10) || 1;
                          setGoalPeriod(pVal);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              period: pVal,
                            },
                          }));
                        }}
                        disabled={!canManageEvents || !isGoalUnlocked}
                        sx={{ flex: 1 }}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                      <TextField
                        size="small"
                        label="Game Clock"
                        value={goalClock}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoalClock(val);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              clock: val,
                            },
                          }));
                        }}
                        disabled={!canManageEvents || !isGoalUnlocked}
                        sx={{ flex: 1 }}
                        placeholder="12:45"
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                      <TextField
                        size="small"
                        label="Score After"
                        value={goalScore}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoalScore(val);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              score: val,
                            },
                          }));
                        }}
                        disabled={!canManageEvents || !isGoalUnlocked}
                        sx={{ flex: 1 }}
                        placeholder="3-2"
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small"
                        label="Scorer"
                        fullWidth
                        value={goalScorer}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoalScorer(val);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              scorer: val,
                            },
                          }));
                        }}
                        disabled={!canManageEvents || !isGoalUnlocked}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                      <TextField
                        size="small"
                        label="Jersey #"
                        value={goalJersey}
                        onChange={(e) => {
                          const val = e.target.value;
                          setGoalJersey(val);
                          setGoalDrafts((prev) => ({
                            ...prev,
                            [goalDraftKey]: {
                              ...(prev[goalDraftKey] || {}),
                              jersey: val,
                            },
                          }));
                        }}
                        disabled={!canManageEvents || !isGoalUnlocked}
                        sx={{ width: 85 }}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                    </Stack>

                    <TextField
                      size="small"
                      label="Assists"
                      fullWidth
                      value={goalAssists}
                      onChange={(e) => {
                        const val = e.target.value;
                        setGoalAssists(val);
                        setGoalDrafts((prev) => ({
                          ...prev,
                          [goalDraftKey]: {
                            ...(prev[goalDraftKey] || {}),
                            assists: val,
                          },
                        }));
                      }}
                      disabled={!canManageEvents || !isGoalUnlocked}
                      placeholder="e.g. Lewis, Papalia"
                      slotProps={{ input: { style: { color: '#fff' } } }}
                    />

                    <TextField
                      size="small"
                      label="Notes / Play Description"
                      fullWidth
                      multiline
                      rows={2}
                      value={goalDesc}
                      onChange={(e) => {
                        const val = e.target.value;
                        setGoalDesc(val);
                        setGoalDrafts((prev) => ({
                          ...prev,
                          [goalDraftKey]: {
                            ...(prev[goalDraftKey] || {}),
                            desc: val,
                          },
                        }));
                      }}
                      disabled={!canManageEvents || !isGoalUnlocked}
                      slotProps={{ input: { style: { color: '#fff' } } }}
                    />

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={() => onJumpToGoal(selectedGoalObj.videoTimestamp)}
                        sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.75)' }}
                      >
                        Jump to Goal
                      </Button>
                      {onDownloadClipPrompt && (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          startIcon={<DownloadIcon fontSize="small" />}
                          onClick={() => {
                            const startSec = parseTimeInput(goalClipStartInput) || Math.max(0, (selectedGoalObj.videoTimestamp || 0) - 15);
                            const endSec = parseTimeInput(goalClipEndInput) || ((selectedGoalObj.videoTimestamp || 0) + 5);
                            const goalAsClip: TimelineClip = {
                              id: selectedGoalObj.eventId || selectedGoalObj.id || `goal-${selectedGoalObj.goalNumber}`,
                              title: `Goal #${selectedGoalObj.goalNumber}: ${getGoalTeamName(selectedGoalObj, isJetsHome, game?.opponent, game?.targetTeam)}`,
                              startTime: startSec,
                              endTime: endSec,
                              duration: Math.max(0, endSec - startSec),
                              notes: selectedGoalObj.description || '',
                              eventType: 'clip',
                            };
                            onDownloadClipPrompt(goalAsClip);
                          }}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Download Clip
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
              );
            })()}

            {/* NOTE PROPERTY INSPECTOR */}
            {selectedTimelineItem?.type === 'note' && selectedNoteObj && (
              <Card
                data-testid="note-inspector-card"
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: '#1c1c1f',
                  borderColor: '#f59e0b',
                  color: '#fff',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        icon={<StickyNote2Icon sx={{ fontSize: '13px !important' }} />}
                        label="Note"
                        size="small"
                        sx={{ bgcolor: '#f59e0b', color: '#000', fontWeight: 700 }}
                      />
                      <Chip
                        label={selectedNoteObj.generalAccess === 'public' ? 'Public' : (selectedNoteObj.shares && selectedNoteObj.shares.length > 0 ? `Shared (${selectedNoteObj.shares.length})` : 'Private')}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.66rem',
                          height: 20,
                          color: selectedNoteObj.generalAccess === 'public' ? '#4ade80' : (selectedNoteObj.shares && selectedNoteObj.shares.length > 0 ? '#38bdf8' : 'rgba(255,255,255,0.7)'),
                          borderColor: selectedNoteObj.generalAccess === 'public' ? 'rgba(74, 222, 128, 0.4)' : (selectedNoteObj.shares && selectedNoteObj.shares.length > 0 ? 'rgba(56, 189, 248, 0.4)' : 'rgba(255,255,255,0.2)'),
                        }}
                      />
                      <Chip
                        icon={isNoteUnlocked ? <LockOpenIcon sx={{ fontSize: '13px !important' }} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                        label={isNoteUnlocked ? "Unlocked (Editing)" : "Note Locked"}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          borderColor: isNoteUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                          color: isNoteUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                        }}
                      />
                      <Tooltip title={`Event ID: ${selectedNoteObj.id} (click to copy)`}>
                        <Chip
                          data-testid="inspector-event-id"
                          label={`ID: ${selectedNoteObj.id}`}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard.writeText(String(selectedNoteObj.id)).catch(() => {});
                            }
                          }}
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            borderColor: 'rgba(255,255,255,0.14)',
                            color: 'rgba(255,255,255,0.45)',
                            '&:hover': {
                              color: 'rgba(255,255,255,0.8)',
                              borderColor: 'rgba(255,255,255,0.35)',
                            },
                          }}
                        />
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {!isNoteUnlocked && (
                        <Tooltip title="Unlock note to edit timestamp and text">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                              onClick={handleUnlockNote}
                              aria-label="Unlock to Edit"
                              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.9, whiteSpace: 'nowrap' }}
                            >
                              Unlock to Edit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isNoteUnlocked && (
                        <>
                          <Tooltip title="Save note changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                aria-label="Save Note Changes"
                                startIcon={savingNote ? <CircularProgress size={13} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingNote}
                                onClick={handleSaveNote}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                              >
                                Save
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Discard unsaved changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                aria-label="Discard Note Changes"
                                startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingNote}
                                onClick={handleDiscardNote}
                                sx={{ textTransform: 'none', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                              >
                                Discard
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {onOpenShareModal && (
                        <Tooltip title="Share Note">
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            aria-label="Share"
                            startIcon={<ShareIcon sx={{ fontSize: '13px !important' }} />}
                            onClick={() => onOpenShareModal(selectedNoteObj)}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontSize: '0.72rem', py: 0.25, px: 0.8 }}
                          >
                            Share
                          </Button>
                        </Tooltip>
                      )}
                      {onDeleteTimelineEvent && (
                        <Tooltip title="Delete note">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            aria-label="Delete"
                            startIcon={<DeleteIcon sx={{ fontSize: '13px !important' }} />}
                            onClick={() => onDeleteTimelineEvent(selectedNoteObj.id)}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', fontSize: '0.72rem', py: 0.25, px: 0.8 }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      )}
                      {onDeselectItem && (
                        <Tooltip title="Close inspector">
                          <IconButton size="small" onClick={onDeselectItem} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="note-event-type-label">Event Type</InputLabel>
                      <Select
                        labelId="note-event-type-label"
                        value={noteTypeInput}
                        label="Event Type"
                        disabled={!canManageEvents || !isNoteUnlocked}
                        onChange={(e) => setNoteTypeInput(e.target.value as any)}
                      >
                        <MenuItem value="note">Note</MenuItem>
                        <MenuItem value="goal">Goal</MenuItem>
                        <MenuItem value="clip">Clip / Highlight</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Timestamp Field */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Timestamp (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={noteTimeInput}
                          onChange={(e) => setNoteTimeInput(e.target.value)}
                          onFocus={() => {
                            isNoteTimeFocusedRef.current = true;
                          }}
                          onBlur={() => {
                            isNoteTimeFocusedRef.current = false;
                          }}
                          disabled={!canManageEvents || !isNoteUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        <Tooltip title={isNoteUnlocked ? "Set note timestamp to current playhead" : "Unlock to set note timestamp to playhead"}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setNoteTimeInput(formatSeconds(currentTime))}
                              disabled={!canManageEvents || !isNoteUnlocked}
                              sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                            >
                              Playhead
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* Note Text */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Note Text
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Add your note here..."
                        value={noteTextInput}
                        onChange={(e) => setNoteTextInput(e.target.value)}
                        disabled={!canManageEvents || !isNoteUnlocked}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                    </Box>

                    {/* Creator Info */}
                    {selectedNoteObj.creatorEmail && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                        Created by: {selectedNoteObj.creatorEmail}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={() => {
                          if (onJumpToTime) onJumpToTime(selectedNoteObj.time);
                          else onJumpToGoal(selectedNoteObj.time);
                        }}
                        sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.75)' }}
                      >
                        Jump to Note
                      </Button>
                      {onDownloadClipPrompt && (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          startIcon={<DownloadIcon fontSize="small" />}
                          onClick={() => {
                            const nTime = selectedNoteObj.time;
                            const noteClip: TimelineClip = {
                              id: selectedNoteObj.id,
                              title: `Note @ ${formatSeconds(nTime)}: ${selectedNoteObj.text.slice(0, 30) || 'Note'}`,
                              startTime: Math.max(0, nTime - 10),
                              endTime: nTime + 10,
                              duration: 20,
                              notes: selectedNoteObj.text,
                              eventType: 'clip',
                            };
                            onDownloadClipPrompt(noteClip);
                          }}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Download Clip
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* CLIP PROPERTY INSPECTOR */}
            {selectedTimelineItem?.type === 'clip' && selectedClipObj && (
              <Card
                data-testid="clip-inspector-card"
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: '#1c1c1f',
                  borderColor: isClipUnlocked ? '#f59e0b' : '#38bdf8',
                  color: '#fff',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        icon={<ContentCutIcon sx={{ fontSize: '13px !important' }} />}
                        label="Clip"
                        size="small"
                        sx={{ bgcolor: isClipUnlocked ? '#f59e0b' : '#38bdf8', color: '#000', fontWeight: 700 }}
                      />
                      <Chip
                        label={selectedClipObj.generalAccess === 'public' ? 'Public' : (selectedClipObj.shares && selectedClipObj.shares.length > 0 ? `Shared (${selectedClipObj.shares.length})` : 'Private')}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.66rem',
                          height: 20,
                          color: selectedClipObj.generalAccess === 'public' ? '#4ade80' : (selectedClipObj.shares && selectedClipObj.shares.length > 0 ? '#38bdf8' : 'rgba(255,255,255,0.7)'),
                          borderColor: selectedClipObj.generalAccess === 'public' ? 'rgba(74, 222, 128, 0.4)' : (selectedClipObj.shares && selectedClipObj.shares.length > 0 ? 'rgba(56, 189, 248, 0.4)' : 'rgba(255,255,255,0.2)'),
                        }}
                      />
                      <Chip
                        icon={isClipUnlocked ? <LockOpenIcon sx={{ fontSize: '13px !important' }} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                        label={isClipUnlocked ? "Unlocked (Editing)" : "Clip Locked"}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          borderColor: isClipUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.2)',
                          color: isClipUnlocked ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                        }}
                      />
                      <Tooltip title={`Event ID: ${selectedClipObj.id} (click to copy)`}>
                        <Chip
                          data-testid="inspector-event-id"
                          label={`ID: ${selectedClipObj.id}`}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard.writeText(String(selectedClipObj.id)).catch(() => {});
                            }
                          }}
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            borderColor: 'rgba(255,255,255,0.14)',
                            color: 'rgba(255,255,255,0.45)',
                            '&:hover': {
                              color: 'rgba(255,255,255,0.8)',
                              borderColor: 'rgba(255,255,255,0.35)',
                            },
                          }}
                        />
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {!isClipUnlocked && (
                        <Tooltip title="Unlock clip to edit title and duration">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<LockOpenIcon sx={{ fontSize: '14px !important' }} />}
                              onClick={handleUnlockClip}
                              aria-label="Unlock to Edit"
                              sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.25, px: 0.9, whiteSpace: 'nowrap' }}
                            >
                              Unlock to Edit
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isClipUnlocked && (
                        <>
                          <Tooltip title="Save clip changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                aria-label="Save Clip Changes"
                                startIcon={savingClip ? <CircularProgress size={13} /> : <LockIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingClip}
                                onClick={handleSaveClip}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                              >
                                Save
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Discard unsaved changes and lock">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                aria-label="Discard Clip Changes"
                                startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
                                disabled={savingClip}
                                onClick={handleDiscardClip}
                                sx={{ textTransform: 'none', color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.72rem', py: 0.25, px: 0.8, whiteSpace: 'nowrap' }}
                              >
                                Discard
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {onOpenShareModal && (
                        <Tooltip title="Share Clip">
                          <IconButton
                            size="small"
                            onClick={() => onOpenShareModal(selectedClipObj)}
                            sx={{ color: '#38bdf8', p: 0.5 }}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDeselectItem && (
                        <Tooltip title="Close inspector">
                          <IconButton size="small" onClick={onDeselectItem} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.25 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1.5 }}>
                    Duration: <strong>{formatSeconds(Math.max(0, parseTimeInput(clipEndInput) - parseTimeInput(clipStartInput)))}</strong> ({Math.max(0, parseTimeInput(clipEndInput) - parseTimeInput(clipStartInput)).toFixed(1)}s)
                  </Typography>

                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="clip-event-type-label">Event Type</InputLabel>
                      <Select
                        labelId="clip-event-type-label"
                        value={clipTypeInput}
                        label="Event Type"
                        disabled={!canManageEvents || !isClipUnlocked}
                        onChange={(e) => setClipTypeInput(e.target.value as any)}
                      >
                        <MenuItem value="clip">Clip / Highlight</MenuItem>
                        <MenuItem value="goal">Goal</MenuItem>
                        <MenuItem value="note">Note</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Title */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Clip Title
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={clipTitleInput}
                        onChange={(e) => setClipTitleInput(e.target.value)}
                        disabled={!isClipUnlocked}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                    </Box>

                    {/* Start Time */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Start Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={clipStartInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setClipStartInput(val);
                            if (selectedClipObj) {
                              setClipDrafts((prev) => ({
                                ...prev,
                                [selectedClipObj.id]: {
                                  ...(prev[selectedClipObj.id] || {
                                    start: val,
                                    end: clipEndInput,
                                    title: clipTitleInput,
                                    notes: clipNotesInput,
                                  }),
                                  start: val,
                                },
                              }));
                            }
                          }}
                          onFocus={() => { isClipStartFocusedRef.current = true; }}
                          onBlur={() => { isClipStartFocusedRef.current = false; }}
                          disabled={!isClipUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        <Tooltip title={isClipUnlocked ? "Set start time to current playhead" : "Unlock to set start time to playhead"}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!isClipUnlocked}
                              onClick={() => {
                                const s = formatSeconds(currentTime);
                                setClipStartInput(s);
                                if (selectedClipObj) {
                                  setClipDrafts((prev) => ({
                                    ...prev,
                                    [selectedClipObj.id]: {
                                      ...(prev[selectedClipObj.id] || {
                                        start: s,
                                        end: clipEndInput,
                                        title: clipTitleInput,
                                        notes: clipNotesInput,
                                      }),
                                      start: s,
                                    },
                                  }));
                                }
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                            >
                              Playhead
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* End Time */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        End Time (MM:SS or seconds)
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          value={clipEndInput}
                          onChange={(e) => {
                            const val = e.target.value;
                            setClipEndInput(val);
                            if (selectedClipObj) {
                              setClipDrafts((prev) => ({
                                ...prev,
                                [selectedClipObj.id]: {
                                  ...(prev[selectedClipObj.id] || {
                                    start: clipStartInput,
                                    end: val,
                                    title: clipTitleInput,
                                    notes: clipNotesInput,
                                  }),
                                  end: val,
                                },
                              }));
                            }
                          }}
                          onFocus={() => { isClipEndFocusedRef.current = true; }}
                          onBlur={() => { isClipEndFocusedRef.current = false; }}
                          disabled={!isClipUnlocked}
                          slotProps={{ input: { style: { color: '#fff', fontFamily: 'monospace' } } }}
                        />
                        <Tooltip title={isClipUnlocked ? "Set end time to current playhead" : "Unlock to set end time to playhead"}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!isClipUnlocked}
                              onClick={() => {
                                const s = formatSeconds(currentTime);
                                setClipEndInput(s);
                                if (selectedClipObj) {
                                  setClipDrafts((prev) => ({
                                    ...prev,
                                    [selectedClipObj.id]: {
                                      ...(prev[selectedClipObj.id] || {
                                        start: clipStartInput,
                                        end: s,
                                        title: clipTitleInput,
                                        notes: clipNotesInput,
                                      }),
                                      end: s,
                                    },
                                  }));
                                }
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}
                            >
                              Playhead
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* Notes / Description */}
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                        Notes / Description
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={clipNotesInput}
                        onChange={(e) => setClipNotesInput(e.target.value)}
                        disabled={!isClipUnlocked}
                        slotProps={{ input: { style: { color: '#fff' } } }}
                      />
                    </Box>

                    {/* Preview & Download Buttons */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        variant="outlined"
                        color="info"
                        fullWidth
                        startIcon={<PlayArrowIcon />}
                        onClick={() => {
                          if (onJumpToTime) onJumpToTime(selectedClipObj.startTime);
                          else onJumpToGoal(selectedClipObj.startTime);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Preview Clip
                      </Button>
                      {onDownloadClipPrompt && (
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          startIcon={<DownloadIcon />}
                          onClick={() => onDownloadClipPrompt(selectedClipObj)}
                          sx={{ textTransform: 'none', bgcolor: '#f59e0b', color: '#000', '&:hover': { bgcolor: '#fbbf24' } }}
                        >
                          Download
                        </Button>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      {onOpenShareModal && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          fullWidth
                          startIcon={<ShareIcon />}
                          onClick={() => onOpenShareModal(selectedClipObj)}
                          sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.8)' }}
                        >
                          Share Controls
                        </Button>
                      )}
                      {onDeleteTimelineEvent && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => onDeleteTimelineEvent(selectedClipObj.id)}
                          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
